/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

'use strict';

import Swiper from 'swiper';
import BaseControl from 'scripts/base-control';
import can from 'can';
import _ from 'lodash';
import appContext from 'scripts/app-context';
import DomEventsManager from 'scripts/dom-events-manager';
import device from 'scripts/utils/device';
import 'components/dfp-advert-unit/dfp-advert-unit';
import { adsMap } from 'components/dfp-advert-unit/dfp-advert-unit';

const THUMBS_SLIDE_SPACE = 7;
const SLIDE_CAPTION_LENGTH = 260;
const clampSlideCaption = (caption = '') => {
    if (caption.length <= SLIDE_CAPTION_LENGTH) {
        return caption;
    }

    return caption.substring(0, 260) + '...';
};

export const PhotoGallery = BaseControl.extend({
    gallery: null,
    galleryTop: null,
    galleryThumbs: null,
    previewImagesSlider: null,
    showThumbnails: false,
    isFullscreen: false,
    visible: false,
    isAdsAdded: false,
    changeSlideMessageTime: null,

    init() {
        this._super();

        this.$galleryTopContainer = this.$element.find(
            '.swiper-container.gallery-top',
        );
        this.$loader = this.$element.find('.s-loader');
        this.$imageInfoContainer = this.$element.find('.s-image-info');
        this.$imageCaption = this.$element.find('.s-slide-title');
        this.$imageIndex = this.$element.find('.s-slide-index');
        this.$imageIndexMessage = this.$element.find('.s-slide-index-message');
        this.$galleryThumbsWrapper = this.$element.find(
            '.gallery-thumbs-wrapper',
        );
        this.$galleryThumbsContainer = this.$element.find(
            '.swiper-container.gallery-thumbs',
        );
        this.initCollectionItems();
        this.isEmbed = this.$element.hasClass('embed');
        this.isMobile = this.options.isMobile;

        this.$galleryTopContainer
            .find('.swiper-slide')
            .each((i, el) =>
                this.$('.s-slide-desc-item--position', el).html(
                    `${i + 1} of ${this.gallery.length}`,
                ),
            );

        DomEventsManager.subscribe(this);

        if (this.$galleryTopContainer.find('.swiper-slide').length > 1) {
            this.initTopSwiper();
        } else {
            this.$element.addClass('single-slide');
            if (!this.gallery[0].image.caption) {
                this.$element.addClass('no-description');
            }
        }

        if (this.options.expanded) {
            if (
                !(
                    window.CQ &&
                    [
                        window.CQ.WCM.MODE_EDIT,
                        window.CQ.WCM.MODE_DESIGN,
                    ].indexOf(window.CQ.WCM.getMode()) > -1
                )
            ) {
                this.toggleFullscreen(true, true);
            }
        }

        window.addEventListener('keydown', (e) => {
            if (e.which === 27) {
                // ESC key
                this.onEscKeyPress();
            }
        });
    },

    insertTopAds() {
        const index = this.getAdIndex();

        if (this.isAdsAdded) {
            return;
        }

        const view = can.stache(
            `
                <span class='swiper-dfp-container'>
                <dfp-advert-unit
                    reload-interval='{{reloadInterval}}'
                    ad-unit-path='{{adUnitPath}}'
                    {sizes}='sizes'
                    {responsive}='responsive'
                    (didnotrender)="onAdDidNotRender()"
                />
                </span>
                        `,
        )({
            sizes: this.options.ad.sizes,
            adUnitPath: this.options.ad.id,
            responsive: true,
            reloadInterval: 5,
            onAdDidNotRender: () => this.removeAds(),
        });

        can.$(`<div class='swiper-slide ad' />`)
            .append(view)
            .insertAfter(
                this.$galleryTopContainer.find('.swiper-slide').eq(index),
            );
        this.insertThumbAds();
        this.isAdsAdded = true;
        setTimeout(() => {
            this.$galleryTopContainer
                .find('.swiper-slide')
                .find('.swiper-dfp-container')
                .addClass('hidden-ads');
        }, 3000);
    },

    removeAds() {
        if (!this.isAdsAdded) {
            return;
        }

        const index = this.$galleryTopContainer
            .find('.swiper-slide.ad')
            .index();
        this.galleryTop.removeSlide(index);
        this.galleryTop.update();
        if (typeof this.galleryTop.updateSlides === 'function') {
            this.galleryTop.updateSlides();
        }
        // remove hidden slide from gallery-thumbs even thumbs swiper didn't init
        this.$galleryThumbsContainer.find('.swiper-slide.is-hidden').remove();
        this.setActiveSlide(this.galleryTop.activeIndex);
        this.isAdsAdded = false;
    },

    insertThumbAds() {
        const index = this.getAdIndex();

        can.$(`<div class='swiper-slide is-hidden' />`).insertAfter(
            this.$galleryThumbsContainer.find('.swiper-slide').eq(index),
        );
    },

    initCollectionItems() {
        const isInfographic = this.options.infographic;
        this.gallery = isInfographic
            ? [{ image: this.options.coverImage }]
            : this.options.images;

        this.slidesCount = this.gallery.length;
    },

    getAdIndex() {
        const adsIndex = Number(this.options.ad.slideNum) - 1;
        const galleryLength = this.gallery.length - 1;
        return adsIndex > galleryLength ? galleryLength : adsIndex;
    },

    // Swiper gallery initialization
    initTopSwiper(initialSlide = 0) {
        if (this.galleryTop) {
            return;
        }

        can.route.ready();
        const routeSlide = can.route.attr('slide');

        if (
            this.options.ad &&
            this.options.ad.id &&
            !adsMap.get(this.options.ad.id) &&
            !device.isTouch
        ) {
            this.insertTopAds();
        }

        this.galleryTop = new Swiper(this.$galleryTopContainer, {
            slidesPerView: 1,
            spaceBetween: 0,
            preventClicks: false,
            initialSlide: routeSlide || initialSlide,
            observer: true,
            watchSlidesVisibility: true,
            zoom: {
                toggle: false,
            },
            keyboard: {
                enabled: true,
            },
            navigation: {
                prevEl: this.$galleryTopContainer.find('.swiper-button-prev'),
                nextEl: this.$galleryTopContainer.find('.swiper-button-next'),
            },
            on: {
                init: () => {
                    this.hideSlideImages();
                },
            },
        });

        this.galleryTop.on('transitionEnd', () => {
            this.onTransitionEnd(this.galleryTop);
            this.hideSlideImages();
        });
    },

    hideSlideImages() {
        this.$galleryTopContainer
            .find('.swiper-slide img')
            .attr('aria-hidden', true);
        this.$galleryTopContainer
            .find('.swiper-slide button')
            .attr('aria-hidden', true);
        this.$galleryTopContainer
            .find('.swiper-slide-visible img')
            .attr('aria-hidden', false);
        this.$galleryTopContainer
            .find('.swiper-slide-visible button')
            .attr('aria-hidden', false);
    },

    hideFocusHiddenSlides() {
        this.$galleryThumbsContainer
            .find('.swiper-slide')
            .find('a')
            .attr('aria-hidden', true);
        this.$galleryThumbsContainer
            .find('.swiper-slide')
            .find('button')
            .attr('aria-hidden', true);
        this.$galleryThumbsContainer
            .find('.swiper-slide-visible')
            .find('a')
            .attr('aria-hidden', false);
        this.$galleryThumbsContainer
            .find('.swiper-slide-visible')
            .find('button')
            .attr('aria-hidden', false);
    },

    initThumbsSwiper() {
        this.galleryThumbs = new Swiper(this.$galleryThumbsContainer, {
            spaceBetween: THUMBS_SLIDE_SPACE,
            slidesPerView: 'auto',
            slidesPerGroup: 2,
            touchRatio: 0.2,
            freeMode: true,
            slideToClickedSlide: false,
            watchSlidesVisibility: true,
            navigation: {
                nextEl: this.$galleryThumbsWrapper.find('.swiper-button-next'),
                prevEl: this.$galleryThumbsWrapper.find('.swiper-button-prev'),
            },
            on: {
                init: () => {
                    this.hideFocusHiddenSlides();
                    this.trapFocus();
                },
                slideChangeTransitionEnd: () => {
                    this.hideFocusHiddenSlides();
                    this.trapFocus();
                    if (
                        !this.$galleryThumbsWrapper
                            .find('.swiper-button-next')
                            .is(':visible')
                    ) {
                        this.$galleryThumbsWrapper
                            .find('.swiper-button-prev')
                            .focus();
                    }
                },
            },
        });

        this.updateGalleryThumbsSlides(this.galleryTop.activeIndex);
    },

    onTransitionEnd(swiper) {
        let activeIndex = swiper.activeIndex;

        if (this.galleryThumbs) {
            this.galleryThumbs.slideTo(activeIndex);
        }
        this.updateGalleryThumbsSlides(activeIndex);
        this.setActiveSlide(activeIndex);
    },

    updateGalleryThumbsSlides(activeIndex) {
        let index = parseInt(activeIndex);

        index = isNaN(index) ? 0 : index;

        this.$galleryThumbsContainer
            .find('.expanded-slide')
            .removeClass('expanded-slide')
            .removeAttr('aria-current');
        this.$galleryThumbsContainer
            .find('.swiper-slide')
            .eq(index)
            .addClass('expanded-slide')
            .attr('aria-current', 'true');
    },

    setActiveSlide(slideIndex = 0) {
        const slideId = this.$galleryTopContainer
            .find('.swiper-slide')
            .eq(slideIndex)
            .data('slide-id');
        if (_.isNil(slideId)) {
            this.$imageCaption.text('');
            this.$imageIndex.text('');
            this.$imageIndexMessage.text('');
            return;
        }

        if (!this.isEmbed) {
            can.route.ready();
            can.route.attr({ slide: slideId });
        }

        const caption = _.get(this.gallery, `${slideId}.image.caption`);
        const formattedCaption = clampSlideCaption(caption);

        this.$imageCaption.text(formattedCaption);
        this.$imageIndex.text(`${slideId + 1} of ${this.gallery.length}`);
        this.$imageIndexMessage.text(
            `Item ${slideId + 1} of ${this.gallery.length}`,
        );

        if (this.changeSlideMessageTime) {
            clearTimeout(this.changeSlideMessageTime);
        }

        this.changeSlideMessageTime = setTimeout(() => {
            this.$imageIndexMessage.text('');
        }, 5000);
    },

    thumbnailsToggle(stateOverride) {
        this.showThumbnails =
            stateOverride !== undefined ? stateOverride : !this.showThumbnails;
        this.$element.toggleClass('thumbnails-shown', this.showThumbnails);
        this.$element
            .find('.js-thumbs')
            .attr('aria-expanded', this.showThumbnails);
        this.showThumbnails
            ? this.$galleryThumbsWrapper.slideDown({
                  duration: 150,
                  easing: 'linear',
              })
            : this.$galleryThumbsWrapper.slideUp({
                  duration: 150,
                  easing: 'linear',
              });
        this.updateThumbsSlider();
        this.$galleryThumbsWrapper
            .find('image-cloudinary')
            .each((i, component) => {
                this.$(component)
                    .viewModel()
                    .recalculateSize(component);
                this.$(component)
                    .viewModel()
                    .show();
            });
    },

    getCurrentSlideIndex() {
        let slide = this.galleryTop
            ? this.galleryTop.activeIndex
            : this.isEmbed
            ? parseInt(can.route.attr('slide', 0))
            : 0;

        if (isNaN(slide)) {
            slide = 0;
        }

        return slide;
    },

    updateSliders() {
        setTimeout(
            function() {
                this.updateSlider();
                this.updateThumbsSlider();
            }.bind(this),
            250,
        );
    },

    updateSlider() {
        const slide = this.getCurrentSlideIndex();

        if (this.galleryTop) {
            this.galleryTop.update();
        } else this.initTopSwiper(slide);

        this.setActiveSlide(slide);
    },

    updateThumbsSlider() {
        const slide = this.getCurrentSlideIndex();

        if (this.galleryThumbs) {
            this.galleryThumbs.detachEvents();
            this.galleryThumbs.destroy();
            this.galleryThumbs = null;
        }

        if (this.checkThumbsFitness()) {
            this.$galleryThumbsWrapper.addClass('thumbs-fit');
        } else {
            this.$galleryThumbsWrapper.removeClass('thumbs-fit');
            this.initThumbsSwiper();
        }

        this.updateGalleryThumbsSlides(slide);

        if (this.galleryThumbs) {
            this.galleryThumbs.slideTo(slide);
        }
    },

    checkThumbsFitness() {
        const thumbsWidth = this.$galleryThumbsContainer
            .find('.swiper-slide')
            .toArray()
            .reduce(
                (prev, el) =>
                    prev +
                    this.$(el).width() +
                    (prev > 0 ? THUMBS_SLIDE_SPACE : 0),
                0,
            );

        return thumbsWidth <= this.$galleryThumbsContainer.width();
    },

    toggleFullscreen(stateOverride, isLoaded = false) {
        let that = this;

        if (this.isMobile) {
            return;
        }
        this.isFullscreen =
            stateOverride !== undefined ? stateOverride : !this.isFullscreen;
        appContext.model.dispatch('page-scroll-toggle', [this.isFullscreen]);
        this.$element.toggleClass('is-full-screen', this.isFullscreen);
        this.$element
            .find('.js-fullscreen')
            .attr('aria-expanded', this.isFullscreen);

        setTimeout(function() {
            that.$loader.fadeOut('slow');
        }, 500);

        if (
            this.isEmbed &&
            ['small', 'extra-small'].indexOf(
                appContext.model.screen.attr('screenSize'),
            ) > -1
        ) {
            this.thumbnailsToggle(false);
        }

        this.updateSliders();

        if (this.isFullscreen) {
            setTimeout(function() {
                that.$galleryTopContainer
                    .find('image-cloudinary')
                    .each(function(i, component) {
                        that.$(component)
                            .viewModel()
                            .recalculateSize(component);
                        that.$(component)
                            .viewModel()
                            .show();
                    });
            }, 250);
            setTimeout(() => {
                if (!isLoaded) {
                    this.element.find('.js-fullscreen').focus();
                }
                this.trapFocus();
                document
                    .getElementsByTagName('body')[0]
                    .addEventListener('slidetransitionend', () => {
                        this.trapFocus();
                    });
            }, 3000);
        } else {
            $('.is-full-screen').off('transitionend');
        }
    },

    getFocusableSelector() {
        return 'a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input[type="text"]:not([disabled]), input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled]), select:not([disabled]), div[role="button"], iframe';
    },

    getFirstFocusableEl() {
        const topFocusableEls = this.$element
            .find('.s-gallery-control')
            .find(this.getFocusableSelector());
        return topFocusableEls[0];
    },

    isMiddleSliderAvailable() {
        return (
            this.$element
                .find('.s-tag-based-related-content-slider')
                .is(':visible') &&
            !this.$element.hasClass('is-hidden-description')
        );
    },

    showAllThumbSlides() {
        this.$galleryThumbsContainer
            .find('.swiper-slide')
            .find('a')
            .attr('aria-hidden', false);
        this.$galleryThumbsContainer
            .find('.swiper-slide')
            .find('button')
            .attr('aria-hidden', false);
    },

    getLastFocusableEl() {
        let focusableEls = this.$galleryTopContainer
            .find(this.getFocusableSelector())
            .not('.swiper-button-disabled');

        if (this.$galleryThumbsWrapper.is(':visible')) {
            if (this.$galleryThumbsWrapper.hasClass('thumbs-fit')) {
                this.showAllThumbSlides();
                focusableEls = this.$galleryThumbsWrapper
                    .find('.gallery-thumbs')
                    .find(this.getFocusableSelector());
            } else {
                focusableEls = this.$galleryThumbsWrapper
                    .find(this.getFocusableSelector())
                    .not('.swiper-button-disabled');
            }
        } else if (this.isMiddleSliderAvailable()) {
            focusableEls = this.$element
                .find('.s-tag-based-related-content-slider')
                .find(this.getFocusableSelector())
                .not('.swiper-button-disabled');
        }

        return focusableEls[focusableEls.length - 1];
    },

    trapFocus() {
        this.$element.off('keydown');
        const KEY_CODE_TAB = 9;
        const firstFocusableEl = this.getFirstFocusableEl();
        const lastFocusableEl = this.getLastFocusableEl();
        const handleTab = (e) => {
            const isTabPressed = e.key === 'Tab' || e.keyCode === KEY_CODE_TAB;
            if (!isTabPressed) {
                return;
            }
            if (e.shiftKey) {
                if (document.activeElement === firstFocusableEl) {
                    lastFocusableEl.focus();
                    e.preventDefault();
                }
            } else {
                if (document.activeElement === lastFocusableEl) {
                    firstFocusableEl.focus();
                    e.preventDefault();
                }
            }
        };
        if (this.isFullscreen) {
            this.$element.on('keydown', handleTab);
        }
    },

    onFirstShow() {
        this.visible = true;
        this.updateSliders();
    },

    onWindowResize() {
        this.updateSliders();
    },

    onEscKeyPress() {
        if (this.isFullscreen) {
            this.$loader.show();
            this.toggleFullscreen();
        }
    },

    '.s-gallery-short click'() {
        this.$loader.show();
        this.toggleFullscreen();
    },

    '.js-fullscreen click'() {
        this.$loader.show();
        this.toggleFullscreen();
    },

    '.js-thumbs click'() {
        this.thumbnailsToggle();
        this.trapFocus();
    },

    '.swiper-container.gallery-thumbs .swiper-slide click'($el) {
        this.setActiveSlide($el.index());
        this.galleryTop.slideTo($el.index());
    },

    '.s-gallery-hidden-start-focus blur'($el) {
        $el.attr('tabindex', '-1');
    },

    '.swiper-container.gallery-top .swiper-slide click'() {
        this.$element.toggleClass('is-hidden-description');
        this.$element.hasClass('is-hidden-description')
            ? this.$imageInfoContainer.fadeOut({
                  duration: 150,
                  easing: 'linear',
              })
            : this.$imageInfoContainer.fadeIn({
                  duration: 150,
                  easing: 'linear',
              });
        this.trapFocus();
    },
});
